import { useQuery } from '@tanstack/react-query'
import { reactQueryKeys } from 'reactQueryKeys/reactQueryKeys'
import { getCustomerWallet } from 'services/CustomerService'
import { getDepositAccounts } from 'services/WalletsService'
import { DepositBankAccount } from 'types/BankAccountsDetails'
import { QueryMetaType } from 'types/reactQuery'
import { Currencies, ErrorAPI } from 'types/types'
import { logSentryError } from 'utils/sentry'
import { mapPaymentInstrumentsToDepositBankAccounts } from 'utils/wallets/mapPaymentInstrumentToDepositBankAccount'

const filterDepositBankAccounts = (
  accounts: DepositBankAccount[],
  useLsid: boolean
): DepositBankAccount[] => {
  return accounts?.filter(bankAccount => {
    if (bankAccount?.currency !== Currencies.GBP) return true
    if (!bankAccount?.accountNumberFormat) return true

    if (!bankAccount?.isSwift && bankAccount?.accountNumberFormat === 'SCAN') return true
    if (!bankAccount?.isSwift && bankAccount?.accountNumberFormat === 'ABA' && useLsid) return true
    if (bankAccount?.isSwift && bankAccount?.accountNumberFormat === 'SWIFT') return true
    return false
  })
}

interface UseDepositAccountsQueryOptions {
  walletId?: string
  enabled?: boolean
  walletLsid?: string
  useLedgerV2?: boolean
}

export const useDepositAccountsQuery = ({
  walletId,
  enabled = false,
  walletLsid: lsid,
  useLedgerV2: isLedgerV2 = false,
}: UseDepositAccountsQueryOptions) => {
  const useLsid = !!(lsid && isLedgerV2)
  const meta: QueryMetaType = {
    errorAdditionalData: useLsid ? { lsid } : { walletId },
  }

  return useQuery<DepositBankAccount[], { data: ErrorAPI }>({
    queryKey: useLsid
      ? reactQueryKeys.depositAccountsV2(lsid!)
      : reactQueryKeys.depositAccounts(walletId!),
    queryFn: async () => {
      if (useLsid) {
        const walletResponse = await getCustomerWallet(lsid)
        if (!walletResponse?.paymentInstruments?.length) {
          logSentryError('Error: useDepositAccountsQuery - paymentInstruments is empty', null, {
            lsid,
          })
        }
        return mapPaymentInstrumentsToDepositBankAccounts(
          walletResponse.paymentInstruments,
          walletResponse.balance?.currency
        )
      }
      const accounts = await getDepositAccounts(walletId!)
      if (!accounts?.length) {
        logSentryError('Error: useDepositAccountsQuery - deposit accounts is empty', null, {
          walletId,
        })
      }
      return accounts
    },
    enabled,
    meta,
    select: (data: DepositBankAccount[]) => filterDepositBankAccounts(data, useLsid),
  })
}
